import React from 'react';
import dayjs from 'dayjs';
import { Badge, Space, Tag } from 'antd';
import { isUndefined } from './commonUtils';
import { AbstractAudit, ApplicationRole, EmployeeDTO, ExtendedBillItemDTO, NotificationType, ReservationStatus, Status, Unit } from 'types';
import { SizeType } from 'antd/lib/config-provider/SizeContext';
import { LegacyButtonType } from 'antd/lib/button/button';
import { SaveOutlined } from '@ant-design/icons';

export const DATE_INFINITE = dayjs('3000-01-01');
const EMPTY_MESSAGE = React.createElement('span', { style: { color: 'red' } }, 'Nevyplněno');

export const getTextOrEmptyMessage = (sth?: string | number, suffix?: string) => {
    return isUndefined(sth) ? EMPTY_MESSAGE : suffix ? sth + ' ' + suffix : sth;
};

export const getButtonProps = (okText: string, okDanger: boolean = false) => {
    const size: SizeType = window.innerWidth < 768 ? 'large' : 'middle';

    return {
        okText: okText,
        okType: 'primary' as LegacyButtonType,
        cancelText: 'Zrušit',
        okButtonProps: { danger: okDanger, icon: React.createElement(SaveOutlined), size },
        cancelButtonProps: { size },
    };
};

/** BADGES UTILS */
export const getReservationBadge = (status: ReservationStatus | string | undefined) => {
    switch (status) {
        case 'A':
            return React.createElement(Badge, { color: 'green', status: 'processing', text: 'Potvrzená' });
        case 'N':
            return React.createElement(Badge, { color: 'orange', text: 'Nepotvrzená' });
        case 'D':
            return React.createElement(Badge, { color: 'red', text: 'Odmítnutá' });
        case 'C':
            return React.createElement(Badge, { color: 'red', text: 'Zrušená' });
        default:
            //X
            return React.createElement(Badge, { color: 'red', text: 'Nedostavil se' });
    }
};

export const getReservationText = (status: ReservationStatus | string | undefined) => {
    switch (status) {
        case 'A':
            return 'Potvrzená';
        case 'N':
            return 'Nepotvrzená';
        case 'D':
            return 'Odmítnutá';
        case 'C':
            return 'Zrušená';
        default:
            //X
            return 'Nedostavil se';
    }
};

export const IconText = ({ icon, text }: { icon: React.FC; text: string }) => {
    return React.createElement(Space, { children: [React.createElement(icon), text] });
};

export const getLastModifierWithDate = <T extends AbstractAudit>(data?: T) => {
    if (isUndefined(data)) return getTextOrEmptyMessage();

    return isUndefined(data!.modified_by)
        ? data!.created_by + ' v ' + convertToFFDateTime(data!.created_date)
        : data!.modified_by + ' v ' + convertToFFDateTime(data!.modified_date);
};

export const getLastModifier = <T extends AbstractAudit>(data?: T) => {
    if (isUndefined(data)) return getTextOrEmptyMessage();

    return isUndefined(data!.modified_by) ? data!.created_by : data!.modified_by!;
};

export const getRoleTag = (role?: ApplicationRole) => {
    switch (role) {
        case 'OWNER':
            return createTag('cyan', 'Vlastník');
        case 'MANAGER':
            return createTag('magenta', 'Manažer');
        default:
            // EMPLOYEE
            return createTag('purple', 'Zaměstnanec');
    }
};

export const getStatusTag = (status?: Status) => {
    switch (status) {
        case 'A':
            return createTag('green', 'Aktivní');
        default:
            // EMPLOYEE
            return createTag('volcano', 'Neaktivní');
    }
};

export const getServiceTypeTag = (is_additional?: boolean) => {
    return is_additional ? createTag('cyan', 'Doplňková služba') : createTag('purple', 'Hlavní služba');
};

export const getEmailTag = (verified?: boolean) => {
    return verified ? createTag('geekblue', 'Ověřený email') : createTag('volcano', 'Neověřený email');
};

export const createTag = (color: string, value: string) => {
    return React.createElement(Tag, { color: color, children: value, key: value });
};

export const convertToFFDateTime = (date?: string) => {
    if (isUndefined(date)) return getTextOrEmptyMessage();

    return dayjs(date).format('DD. MM. YYYY - HH:mm');
};

export const convertToFFDate = (date?: string) => {
    if (isUndefined(date)) return getTextOrEmptyMessage();

    return dayjs(date).format('DD. MM. YYYY');
};

export const getValidityText = (validFrom?: string, validTo?: string) => {
    if (isUndefined(validFrom) || isUndefined(validTo)) return getTextOrEmptyMessage();

    if (dayjs(validFrom).isBefore(dayjs()) && dayjs(validTo).isSame(DATE_INFINITE)) return 'Trvalá platnost';
    if (dayjs(validTo).isSame(DATE_INFINITE)) return `Od ${convertToFFDate(validFrom)}`;
    if (dayjs(validFrom).isBefore(dayjs())) return `Do ${convertToFFDate(validTo)}`;
    return `Od ${convertToFFDate(validFrom)} do ${convertToFFDate(validTo)}`;
};

export const formatNumber = (amount?: number): string => {
    if (isUndefined(amount)) return '0 Kč';

    return amount!
        .toFixed(0)
        .replace(/\B(?=(\d{3})+(?!\d))/g, ' ')
        .concat(' Kč');
};

export const removeMilis = (sth: string) => {
    const ss = sth!.split(':');
    return ss[0] + ':' + ss[1];
};

export const removeMilisDouble = (time_from: string, time_to: string) => {
    return removeMilis(time_from) + ' - ' + removeMilis(time_to);
};

export const getUnitText = (unit: Unit) => {
    switch (unit) {
        case 'ks':
            return 'Kus';
        case 'kg':
            return 'Kilogram';
        case 'g':
            return 'Gram';
        case 'l':
            return 'Litr';
        case 'ml':
            return 'Mililitr';
    }
};

export const getDayName = (day: number) => {
    switch (day) {
        case 1:
            return 'Pondělí';
        case 2:
            return 'Úterý';
        case 3:
            return 'Středa';
        case 4:
            return 'Čtvrtek';
        case 5:
            return 'Pátek';
        case 6:
            return 'Sobota';
        case 7:
            return 'Neděle';
    }
};

export const getNotifTypeName = (type: NotificationType) => {
    switch (type) {
        case 'EMAIL_FAIL':
            return 'Email se nepodařilo odeslat';
        case 'NEW_RESERVATION':
            return 'Nová rezervace';
        case 'CANCEL_RESERVATION':
            return 'Zrušená rezervace';
        case 'FORM_FAIL':
            return 'Chyba na rezervačním formuláři';
    }
};

export const getEmployeeText = (emp?: EmployeeDTO) => {
    if (isUndefined(emp)) return '';

    return isUndefined(emp!.nickname) ? emp!.name + ' ' + emp!.surname : emp!.nickname;
};

export const getItemLabel = (item: ExtendedBillItemDTO) => {
    if (!isUndefined(item.reservation) && isUndefined(item.service)) {
        return 'Rezervace';
    } else if (!isUndefined(item.service)) {
        return item.service!.name;
    } else {
        return item.product_variant!.product.name + ' - ' + item.product_variant!.name;
    }
};
